import { Component, effect, inject, input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { RootCauseFollowupService } from 'src/app/services/root-cause-followup.service';
import { Subscription } from 'rxjs';
import { ChangeStatus } from 'src/app/services/problem.service';

@Component({
  selector: 'app-followup-entry',
  imports: [ReactiveFormsModule, MatFormField, MatButton, MatInput, MatIcon],
  templateUrl: './followup-entry.component.html',
  styleUrl: './followup-entry.component.scss'
})
export class FollowupEntryComponent implements OnInit {
  /*******************************************************
  * Input Injections                                     *
  ********************************************************/
  public fault = input<any>();
  public index = input<number>();

  /*******************************************************
  * Service Injections                                   *
  ********************************************************/
  protected fb = inject(FormBuilder);
  protected svcRootCause = inject(RootCauseFollowupService);

  /*******************************************************
  * Class Variables                                      *
  ********************************************************/
  protected faultFormGroup: FormGroup;
  protected clipboardSet: boolean = false;
  protected currentFault:any;
  protected currentIndex:number = -1;
  protected clipboard$: Subscription | undefined;
  protected clipboardValue:any;
  protected changeStatus:ChangeStatus = ChangeStatus.NO_CHANGES;
  protected statusChanges$: Subscription | undefined;
  protected saveTrigger$: Subscription | undefined;

  constructor() {
    // Initialize the empty form group structure
    this.faultFormGroup = this.fb.group({
      acknowledged: [false],
      note: ['']
    });

    // Use an effect to reactively update the form when the input signal resolves
    effect(() => {
      const index = this.index();
      if (index != undefined) {
        this.currentIndex = index;
      }

      this.currentFault = this.fault();
      this.setDefaultForm();
    });
  }

  ngOnInit(): void {
    this.getClipboard();
    this.setDetectChanges();
  }

  setDefaultForm() {
    //const currentFault = this.fault();
    if (this.currentFault) {
      this.faultFormGroup.patchValue({
        acknowledged: this.currentFault.AcknowledgeDateTime != '' ? true : false,
        note: this.currentFault.AcknowledgeComment ?? ''
      });
      this.faultFormGroup.markAsPristine();
    }
  }

  /**
   * Detect changes to the formgroup, if it detects a change subscribe to the global save trigger and when the save trigger is set 
   * to true save the form if it has changed values and is valid.
   * 
   */
  setDetectChanges() {
    if (this.statusChanges$) this.statusChanges$.unsubscribe();

    // Monitor the FormGroup for changes and set the status in the service so parent component will know if changes have been made
    this.statusChanges$ = this.faultFormGroup.statusChanges.subscribe( 
      (formStatus:string) => 
      {
        let myStatus:ChangeStatus;

        // Only care about the status if the form is dirty (meaning a change has occurred to the data)
        if (this.faultFormGroup.dirty) {
          if (formStatus == 'VALID') {
            myStatus = ChangeStatus.CHANGES_VALID;
          } else {
            myStatus = ChangeStatus.CHANGES_INVALID;
          }
        } else {
          myStatus = ChangeStatus.NO_CHANGES;          
        }

        if (myStatus != this.changeStatus) {
          this.changeStatus = myStatus;
          this.svcRootCause.setChangeStatus(this.currentIndex, myStatus);
        }
      }
    )

    if (this.saveTrigger$) this.saveTrigger$.unsubscribe();

    // Monitor for a signal from parent to save
    this.saveTrigger$ = this.svcRootCause.getSaveTrigger().subscribe(
      (save) => {
        if (save) {
          console.debug("Force save", this.faultFormGroup);
          this.saveEntry();
        }
      }
    );
  }

  saveEntry() {
    this.svcRootCause.acknowledge(this.currentFault, this.faultFormGroup.get('acknowledged')?.value, this.faultFormGroup.get('note')?.value).subscribe(
      (out) => {
        console.debug("Saved Results", out);
        this.faultFormGroup.markAsPristine();
        this.changeStatus = ChangeStatus.NO_CHANGES;
        this.svcRootCause.setChangeStatus(this.currentIndex, this.changeStatus); // Set service change status so parent knows save has been completed
      }
    )
  }

  undoChanges() {
    this.setDefaultForm();
  }

    // Methods for Clip board Operations and Save/Undo operations
  getClipboard() {
    if (this.clipboard$) this.clipboard$.unsubscribe();

    this.clipboard$ = this.svcRootCause.getClipboard().subscribe(
      (value) => {
        this.clipboardValue = value;
        if (value) {
          this.clipboardSet = true;
        }
      }
    );
  }

  setClipboard() {
    this.svcRootCause.setClipboard(this.faultFormGroup.value);
  }

  pasteClipboard() {
    if (this.clipboardValue) {
      this.faultFormGroup.patchValue({
        acknowledged: this.clipboardValue.acknowledged,
        note: this.clipboardValue.note
      });

      this.faultFormGroup.markAsDirty();

      if (this.changeStatus != ChangeStatus.CHANGES_VALID) {
        this.changeStatus = ChangeStatus.CHANGES_VALID;;
        this.svcRootCause.setChangeStatus(this.currentIndex, ChangeStatus.CHANGES_VALID);
      }

    }
  }


}
